import React, { useRef, useState } from 'react'
import { Box, CircularProgress, IconButton, TableCell, TableRow, TextField, Tooltip } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import HistoryIcon from '@mui/icons-material/History'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { setTerm } from './languageTermsApi'
import { useTermRequestFlow } from './useTermRequestFlow'
import ConfirmChangeDialog from './ConfirmChangeDialog'
import UsageChip from './UsageChip'
import RowHistorySlot from './RowHistorySlot'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

// RN6k/DC-17: every icon of the table has a pressable area of at least 40 x 40 px.
const ICON_TARGET = { minWidth: 40, minHeight: 40 }

// DC-35: the field in edition never gets narrower than this (with 20 % columns it was ~40 px at 1512 px).
const MIN_FIELD_WIDTH = 200

const CLASS_MARK_KEY = { outsideCatalogue: 'Outside the catalogue', detached: 'Detached' }

/**
 * Historia 176 — RN6g/RN6h/RN6k. One line of the catalogue table: standard term, inherited term,
 * customised term (edited in place, only in labels with a readable file), usage chip and the two
 * icon actions. `editing` is decided by the table: one cell at a time.
 * Story 204 (RN6.3, RN6.9): a `systemValue` row is editable like a label; `systemValues` (`indexSystemValues` of C5)
 * gives its usage chip, its global confirmation and its markers, and `row.domains` lists the domains of a homonym.
 */
const TermRow = ({
  row = {},
  code = '',
  artifact = {},
  systemValues = NO_SYSTEM_VALUES,
  readOnly = false,
  editing = false,
  onStartEdit = () => {},
  onStopEdit = () => {},
  onTermChanged = () => {},
  onFileUnavailable = () => {},
  onSuccess = () => {},
  usageOpen = false,
  onUsageOpen = () => {},
  onUsageClose = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const hasCustom = row.custom !== undefined
  const editable = (row.termClass === 'label' || row.termClass === 'systemValue') && !readOnly
  // RN9b: what the table paints today for this key: the customised term, or the inherited one when there is none.
  const paintedToday = hasCustom ? row.custom : row.inherited
  // RN6l: which operation is in flight, to give its own success notice (T47 / T48 / T49).
  const operation = useRef('')
  const flow = useTermRequestFlow({
    code,
    artifact,
    systemValues,
    onFileUnavailable,
    onApplied: (termKey, result) => {
      onTermChanged(termKey, result.value)
      onStopEdit()
      // RN6l: a 200 without change (nothing was written) gives no success notice.
      if (result.change !== null) onSuccess(operation.current)
    }
  })

  // DC-35/RN6h: while the cell is in edition it also takes the Usage column (the chip comes back afterwards).
  const cellInEdition = (editing && editable) || flow.pendingRequest

  const startEditing = () => {
    setDraft(hasCustom ? row.custom : '')
    onStartEdit()
  }

  // CD-18: Enter saves; Escape and losing the focus cancel (a save is a global change that may ask
  // for confirmation, so it must never fire by itself). While the confirmation is open the focus is
  // in the dialog, and cancelling it leaves the cell in edition with what was typed (H7).
  // DC-17: the save button and Enter do the same. Shift+Enter is someone asking for a line break: not a save.
  const save = () => {
    if (flow.pendingRequest) return
    // RN13/RN6h: nothing changed (a blank cell over an inherited term counts as nothing) -> no call to Sun.
    if (hasCustom ? draft === row.custom : draft.trim() === '') {
      onStopEdit()
      return
    }
    // RN9b.1: a blank cell paints the inherited term, so that is the `to` of the confirmation title.
    operation.current = 'Term saved'
    flow.submit(row.key, () => setTerm(code, row.key, draft), { from: paintedToday, to: draft.trim() === '' ? row.inherited : draft })
  }

  // RN6h: with the focus on the cell, Enter or F2 put it in edition, as a click does.
  const handleCellKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault()
      startEditing()
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault() // no line break in a term (RN6h)
      if (!event.shiftKey) save()
    } else if (event.key === 'Escape') {
      onStopEdit()
    }
  }

  // RN12: "Undo customisation" always sends '' - Sun decides the deletion.
  const handleUndo = () => {
    operation.current = 'Customisation undone'
    flow.submit(row.key, () => setTerm(code, row.key, ''), { from: paintedToday, to: row.inherited })
  }

  // DC-17/CD-18: pressing ✓ or ✕ is not losing the focus. The buttons keep the focus in the field
  // when pressed with the mouse (mouseDown) and the focus moving to them from the field (Tab) stays
  // inside the edition (relatedTarget); the click then reaches the button.
  const keepFocus = (event) => event.preventDefault()
  const handleBlur = (event) => {
    // currentTarget, not the ref: when the cell is entered from the keyboard the field takes the focus
    // during the commit, before the ref is attached, and that blur must not count as losing the focus.
    if (event.currentTarget.contains(event.relatedTarget)) return
    if (!flow.confirmOpen && !flow.pendingRequest) onStopEdit()
  }

  return (
    <TableRow data-testid={`term-row-${row.key}`} {...(hasCustom ? { 'data-customised': 'true' } : {})}>
      {/* RN6l: a customised row is marked at its left edge, in bold and for screen readers (T43). */}
      <TableCell
        data-testid={`standard-${row.key}`}
        sx={hasCustom ? { boxShadow: (theme) => `inset 4px 0 0 ${theme.palette.primary.main}` } : {}}
      >
        {row.key}
      </TableCell>
      <TableCell data-testid={`inherited-${row.key}`}>{row.inherited}</TableCell>
      <TableCell data-testid={`custom-${row.key}`} colSpan={cellInEdition ? 2 : 1}>
        {cellInEdition
          ? (
            <Box onBlur={handleBlur} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                size='small'
                autoFocus
                sx={{ flex: 1, minWidth: MIN_FIELD_WIDTH }}
                inputProps={{ 'aria-label': row.key }}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={flow.pendingRequest}
                InputProps={{ endAdornment: flow.pendingRequest ? <CircularProgress size={16} /> : null }}
              />
              {/* The span lets the tooltip work while the button is disabled (a save in flight). */}
              <Tooltip title={t_('Save (Enter)')}>
                <span>
                  <IconButton
                    size='small'
                    sx={ICON_TARGET}
                    aria-label={t_('Save (Enter)')}
                    disabled={flow.pendingRequest}
                    onMouseDown={keepFocus}
                    onClick={save}
                  >
                    <CheckIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t_('Cancel (Esc)')}>
                <span>
                  <IconButton
                    size='small'
                    sx={ICON_TARGET}
                    aria-label={t_('Cancel (Esc)')}
                    disabled={flow.pendingRequest}
                    onMouseDown={keepFocus}
                    onClick={onStopEdit}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            )
          : (
            <Box
              role={editable ? 'button' : undefined}
              tabIndex={editable ? 0 : undefined}
              onClick={editable ? startEditing : undefined}
              onKeyDown={editable ? handleCellKeyDown : undefined}
            >
              {hasCustom
                ? <Box component='span' sx={{ fontWeight: 700 }}>{row.custom}</Box>
                : <Box component='span' sx={{ opacity: 0.6 }}>{t_('Inherited')}</Box>}
              {hasCustom ? <Box component='span' sx={visuallyHidden}>{t_('Customised')}</Box> : null}
            </Box>
            )}
        {flow.errorKey !== '' ? <Box role='alert'>{flow.errorMessage}</Box> : null}
        {row.sameAsInherited ? <span>{t_('Same as inherited')}</span> : null}
        {CLASS_MARK_KEY[row.termClass] ? <span>{t_(CLASS_MARK_KEY[row.termClass])}</span> : null}
        {/* Story 204 (S111): a homonym is one row that names every domain it comes from. */}
        {row.domains && row.domains.length > 1 ? <span data-testid={`domains-${row.key}`}>{row.domains.join(' · ')}</span> : null}
      </TableCell>
      {cellInEdition
        ? null
        : (
          <TableCell>
            <UsageChip termKey={row.key} artifact={artifact} systemValues={systemValues} open={usageOpen} onOpen={onUsageOpen} onClose={onUsageClose} />
          </TableCell>
          )}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {/* B1 (RN6g): the two icons never stack. */}
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          {/* RN6k: tooltip and aria-label are the same text. The span lets the tooltip work while the icon is disabled. */}
          <Tooltip title={t_('Undo customisation')}>
            <span>
              <IconButton
                size='small'
                sx={ICON_TARGET}
                aria-label={t_('Undo customisation')}
                disabled={!hasCustom || readOnly || flow.pendingRequest}
                onClick={handleUndo}
              >
                <UndoIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t_('History')}>
            <span>
              <IconButton size='small' sx={ICON_TARGET} aria-label={t_('History')} disabled={flow.pendingRequest} onClick={() => setHistoryOpen(true)}>
                <HistoryIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* RN9: a save or undo that reaches 2+ modules or the common structure asks first (a restore asks in its own panel). */}
        <ConfirmChangeDialog
          open={flow.confirmOpen}
          termKey={row.key}
          from={flow.pendingChange.from}
          to={flow.pendingChange.to}
          artifact={artifact}
          systemValues={systemValues}
          onConfirm={flow.handleConfirm}
          onCancel={flow.handleCancel}
        />
        <RowHistorySlot
          open={historyOpen}
          code={code}
          termKey={row.key}
          currentValue={hasCustom ? row.custom : ''}
          inherited={row.inherited}
          readOnly={readOnly}
          artifact={artifact}
          systemValues={systemValues}
          onClose={() => setHistoryOpen(false)}
          onFileUnavailable={() => {
            setHistoryOpen(false)
            onFileUnavailable()
          }}
          onRestored={(termKey, value, change) => {
            setHistoryOpen(false)
            onTermChanged(termKey, value)
            if (change !== null) onSuccess('Value restored')
          }}
        />
      </TableCell>
    </TableRow>
  )
}

export default TermRow
